import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const PrivacyPage = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  return (
    <div data-testid="privacy-page">
      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-mist">
        <div className="container-custom">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-navy/70 hover:text-berry transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            Terug naar home
          </Link>
          <motion.h1 {...fadeUp} className="heading-1 mb-4">
            Privacyverklaring
          </motion.h1>
          <motion.p {...fadeUp} transition={{ delay: 0.1 }} className="body-large max-w-2xl">
            Hoe wij omgaan met jouw persoonsgegevens.
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto prose prose-lg">
            <motion.div {...fadeUp}>
              <p className="text-navy/70 leading-relaxed mb-6">
                RB Financial Management B.V. respecteert jouw privacy en gaat zorgvuldig om met 
                jouw persoonsgegevens. In deze privacyverklaring leggen wij uit welke gegevens 
                wij verzamelen, waarom wij dat doen en hoe wij deze gegevens beschermen. 
                Deze verklaring is in overeenstemming met de Algemene Verordening Gegevensbescherming (AVG/GDPR).
              </p>

              <h2 className="font-serif text-2xl text-navy mb-4 mt-10">
                1. Wie zijn wij?
              </h2>
              <p className="text-navy/70 leading-relaxed mb-4">
                RB Financial Management B.V. is verantwoordelijk voor de verwerking van jouw persoonsgegevens.
              </p>
              <ul className="text-navy/70 mb-6 space-y-1">
                <li><strong>Bedrijfsnaam:</strong> RB Financial Management B.V.</li>
                <li><strong>KvK-nummer:</strong> 96319704</li>
                <li><strong>E-mail:</strong> rabia@regieopcashflow.nl</li>
              </ul>

              <h2 className="font-serif text-2xl text-navy mb-4 mt-10">
                2. Welke persoonsgegevens verzamelen wij?
              </h2>
              <p className="text-navy/70 leading-relaxed mb-4">
                Wij kunnen de volgende persoonsgegevens verzamelen:
              </p>
              <ul className="text-navy/70 mb-6 space-y-2 list-disc pl-6">
                <li>Naam en contactgegevens (e-mailadres, telefoonnummer)</li>
                <li>Bedrijfsnaam en functie</li>
                <li>Inhoud van berichten die u ons stuurt via het contactformulier</li>
                <li>Technische gegevens (IP-adres, browsertype, apparaatinformatie)</li>
                <li>Websitegebruiksgegevens via cookies (indien u toestemming geeft)</li>
              </ul>

              <h2 className="font-serif text-2xl text-navy mb-4 mt-10">
                3. Waarom verzamelen wij deze gegevens?
              </h2>
              <p className="text-navy/70 leading-relaxed mb-4">
                Wij verwerken jouw persoonsgegevens voor de volgende doeleinden:
              </p>
              <ul className="text-navy/70 mb-6 space-y-2 list-disc pl-6">
                <li>Om contact met je op te nemen naar aanleiding van jouw vraag of aanvraag</li>
                <li>Om een strategisch gesprek in te plannen</li>
                <li>Om onze dienstverlening te kunnen uitvoeren</li>
                <li>Om de website te verbeteren en te analyseren (met jouw toestemming)</li>
                <li>Om te voldoen aan wettelijke verplichtingen</li>
              </ul>

              <h2 className="font-serif text-2xl text-navy mb-4 mt-10">
                4. Hoe lang bewaren wij jouw gegevens?
              </h2>
              <p className="text-navy/70 leading-relaxed mb-6">
                Wij bewaren jouw persoonsgegevens niet langer dan noodzakelijk voor de doeleinden 
                waarvoor zij zijn verzameld:
              </p>
              <ul className="text-navy/70 mb-6 space-y-2 list-disc pl-6">
                <li><strong>Contactformuliergegevens:</strong> maximaal 2 jaar na laatste contact</li>
                <li><strong>Klantgegevens:</strong> 7 jaar na beëindiging samenwerking (wettelijke bewaarplicht)</li>
                <li><strong>Websitestatistieken:</strong> maximaal 26 maanden (geanonimiseerd)</li>
              </ul>

              <h2 className="font-serif text-2xl text-navy mb-4 mt-10">
                5. Met wie delen wij jouw gegevens?
              </h2>
              <p className="text-navy/70 leading-relaxed mb-4">
                Wij delen jouw persoonsgegevens alleen met derden wanneer dit noodzakelijk is:
              </p>
              <ul className="text-navy/70 mb-6 space-y-2 list-disc pl-6">
                <li>Hostingproviders voor het beheren van onze website</li>
                <li>E-mailserviceproviders voor het versturen van berichten</li>
                <li>Analysetools (alleen met jouw toestemming)</li>
                <li>Wanneer wij hiertoe wettelijk verplicht zijn</li>
              </ul>
              <p className="text-navy/70 leading-relaxed mb-6">
                Wij verkopen jouw gegevens nooit aan derden.
              </p>

              <h2 className="font-serif text-2xl text-navy mb-4 mt-10">
                6. Beveiliging van jouw gegevens
              </h2>
              <p className="text-navy/70 leading-relaxed mb-6">
                Wij nemen passende technische en organisatorische maatregelen om jouw persoonsgegevens 
                te beschermen tegen verlies, misbruik of ongeautoriseerde toegang. Dit omvat onder 
                andere SSL-encryptie, beveiligde servers en beperkte toegang tot gegevens.
              </p>

              <h2 className="font-serif text-2xl text-navy mb-4 mt-10">
                7. Jouw rechten
              </h2>
              <p className="text-navy/70 leading-relaxed mb-4">
                Je hebt de volgende rechten met betrekking tot jouw persoonsgegevens:
              </p>
              <ul className="text-navy/70 mb-6 space-y-2 list-disc pl-6">
                <li><strong>Recht op inzage:</strong> Je kunt opvragen welke gegevens wij van je hebben</li>
                <li><strong>Recht op rectificatie:</strong> Je kunt onjuiste gegevens laten corrigeren</li>
                <li><strong>Recht op verwijdering:</strong> Je kunt verzoeken jouw gegevens te verwijderen</li>
                <li><strong>Recht op beperking:</strong> Je kunt de verwerking van jouw gegevens laten beperken</li>
                <li><strong>Recht op overdraagbaarheid:</strong> Je kunt jouw gegevens opvragen in een gangbaar formaat</li>
                <li><strong>Recht op bezwaar:</strong> Je kunt bezwaar maken tegen de verwerking van jouw gegevens</li>
              </ul>
              <p className="text-navy/70 leading-relaxed mb-6">
                Om gebruik te maken van deze rechten kun je contact opnemen via{" "}
                <a href="mailto:rabia@regieopcashflow.nl" className="text-berry hover:underline">
                  rabia@regieopcashflow.nl
                </a>. Wij reageren binnen 30 dagen op jouw verzoek.
              </p>

              <h2 className="font-serif text-2xl text-navy mb-4 mt-10">
                8. Cookies
              </h2>
              <p className="text-navy/70 leading-relaxed mb-6">
                Deze website maakt gebruik van cookies. Voor meer informatie over welke cookies 
                wij gebruiken en hoe je jouw voorkeuren kunt beheren, verwijzen wij je naar ons{" "}
                <Link to="/cookiebeleid" className="text-berry hover:underline">
                  cookiebeleid
                </Link>.
              </p>

              <h2 className="font-serif text-2xl text-navy mb-4 mt-10">
                9. Klachten
              </h2>
              <p className="text-navy/70 leading-relaxed mb-6">
                Heb je een klacht over de verwerking van jouw persoonsgegevens? Neem dan eerst 
                contact met ons op. Komen wij er samen niet uit, dan heb je het recht een 
                klacht in te dienen bij de Autoriteit Persoonsgegevens:{" "}
                <a 
                  href="https://autoriteitpersoonsgegevens.nl" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-berry hover:underline"
                >
                  www.autoriteitpersoonsgegevens.nl
                </a>
              </p>

              <h2 className="font-serif text-2xl text-navy mb-4 mt-10">
                10. Wijzigingen
              </h2>
              <p className="text-navy/70 leading-relaxed mb-6">
                Wij behouden ons het recht voor om deze privacyverklaring te wijzigen. 
                Wijzigingen worden op deze pagina gepubliceerd. Wij raden u aan deze 
                verklaring regelmatig te raadplegen.
              </p>

              <div className="mt-12 p-6 bg-mist rounded-sm">
                <h3 className="font-semibold text-navy mb-2">Vragen?</h3>
                <p className="text-navy/70 text-sm">
                  Heb je vragen over deze privacyverklaring of over hoe wij omgaan met jouw 
                  persoonsgegevens? Neem dan contact met ons op via{" "}
                  <a href="mailto:rabia@regieopcashflow.nl" className="text-berry hover:underline">
                    rabia@regieopcashflow.nl
                  </a>
                </p>
              </div>

              <p className="text-navy/70 text-sm mt-8">
                Laatst bijgewerkt: {new Date().toLocaleDateString('nl-NL', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
